import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import { ErrorModal, SuccessModal } from "@components";

import { CommentType } from "@components";
import {
  BasicInfo,
  Comments,
  Letter,
  SupportingDocs,
  TransferHeader,
} from "./components";

import { useInterchangeTransfer } from "@hooks/HRM/transfer/useInterchangeTransfer";
import { useUser } from "@hooks/useUser";

import { GetEmployeeDetails } from "@utils/getEmployeeDetails";

import {
  interchangeTransfer,
  forwardInterchangeTransferFlow,
} from "@api/interchangeTransfer";

import { ROUTES } from "@constants/routes";
import { STATUS, TRANSFER_STATUS } from "@constants/status.hrm";

import {
  InterchangeTransferDetailsFormDataType,
  InterchangeTransferDetailsType,
} from "@type/HRM/interchangeTransfer";

import { TRANSFER } from "@validations/HRM/transfer/transferValidation";
import {
  convertFormDataForSubmission,
  revertMultiDocumentSingleFileData,
} from "@utils/transferDocumentsDataConvert";
import { Button } from "@pentabd/ui";
import { useTranslation } from "react-i18next";
import {
  INTERCHANGE_TRANSFER,
  interchangeTransferDetailsValidation,
} from "@validations/HRM/interchangeTransfer/interchangeTransferValidation";
import { BasicInfoForTarget } from "./components/BasicInfoForTarget";
import { SupportingDocsForTarget } from "./components/SupportingDocsForTarget";

const {
  HRM: { INTERCHANGE_TRANSFERS },
} = ROUTES;
const { INITIATED, COMPLETED, NEED_TO_VERIFIED } = TRANSFER_STATUS;
const { SUCCESS } = STATUS;
const {
  ASSET_DOCS,
  TRANSFER_DOCS,
  JOINING_LETTER_DOCS,
  NOC_DOCS,
  SUPPORTING_DOCS,
} = TRANSFER;

export default function InterchangeTransferDetails() {
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);
  const [showActionButton, setShowActionButton] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { user } = useUser();
  const { realm_roles: roles, employeeMasterId } = user || {};

  const transfer = useInterchangeTransfer(Number(id));
  const { employeeName, employeePosition, employeeImage } = GetEmployeeDetails(
    transfer?.sourceEmployee?.employee as any,
  );

  const {
    employeeName: targetEmployeeName,
    employeePosition: targetEmployeePosition,
    employeeImage: targetEmployeeImage,
  } = GetEmployeeDetails(transfer?.targetEmployee?.employee as any);

  // useEffect(() => {
  //   if (transfer?.sourceEmployee && transfer?.targetEmployee) {
  //     console.log(
  //       revertMultiDocumentSingleFileData(
  //         transfer?.sourceEmployee,
  //         transfer?.targetEmployee,
  //         [
  //           ASSET_DOCS,
  //           TRANSFER_DOCS,
  //           JOINING_LETTER_DOCS,
  //           NOC_DOCS,
  //           SUPPORTING_DOCS,
  //         ],
  //       ),
  //     );
  //   }
  // }, [transfer]);

  // const canModifyOwnTransfer =
  //   roles.includes("f_per_own_transfer") &&
  //   Number(employeeMasterId) === Number(transfer?.employee?.id);
  const canModifyAllTransfer = roles.includes("f_per_all_transfer");
  // const permissions = canModifyAllTransfer || canModifyOwnTransfer;
  const permissions = canModifyAllTransfer;

  const isCompleted = transfer?.status?.toUpperCase() === COMPLETED;
  const isInitiated = transfer?.status?.toUpperCase() === INITIATED;

  const methods = useForm({
    resolver: yupResolver(interchangeTransferDetailsValidation),
    values: {
      ...revertMultiDocumentSingleFileData(
        transfer?.sourceEmployee,
        transfer?.targetEmployee,
        [
          ASSET_DOCS,
          TRANSFER_DOCS,
          JOINING_LETTER_DOCS,
          NOC_DOCS,
          SUPPORTING_DOCS,
        ],
      ),
    },
  });

  const { handleSubmit, setValue } = methods;

  const onSubmit: SubmitHandler<any> = (
    data: InterchangeTransferDetailsType,
  ) => {
    setIsLoading(true);
    if (
      transfer?.sourceEmployee?.joiningDate &&
      transfer?.targetEmployee?.joiningDate
    ) {
      forwardInterchangeTransferFlow(
        Number(id),
        {
          status:
            transfer?.status?.toUpperCase() === INITIATED
              ? NEED_TO_VERIFIED
              : COMPLETED,
          executionerId: employeeMasterId,
          comments: (data.comments as unknown as string)
            ? (data.comments as unknown as string)
            : "",
        },
        (response) => {
          if (response.status.toUpperCase() === SUCCESS) {
            setIsSuccessModalOpen(true);
          }
          setIsLoading(false);
        },
      );
    } else {
      const convertedData = convertFormDataForSubmission(data);

      interchangeTransfer(
        Number(id),
        convertedData as InterchangeTransferDetailsFormDataType,
        (response) => {
          if (response.status.toUpperCase() === SUCCESS) {
            setIsSuccessModalOpen(true);
          } else {
            setIsErrorModalOpen(true);
          }
          setIsLoading(false);
        },
      );
    }
  };

  const closeSuccessModal = () => {
    setIsSuccessModalOpen(false);
    navigate(INTERCHANGE_TRANSFERS);
  };

  const closeErrorModal = () => setIsErrorModalOpen(false);

  useEffect(() => {
    setValue("status", transfer?.status);
    const isSaved =
      transfer?.sourceEmployee?.joiningDate &&
      transfer?.targetEmployee?.joiningDate;
    setShowActionButton(permissions && isSaved);
    setIsDisabled(!isInitiated || !permissions);
  }, [id, transfer, permissions, isInitiated]);

  return (
    <>
      {/* AFTER EFFECTS MODAL */}
      <SuccessModal
        isModalOpen={isSuccessModalOpen}
        action={closeSuccessModal}
      />
      <ErrorModal isModalOpen={isErrorModalOpen} closeModal={closeErrorModal} />
      {/* AFTER EFFECTS MODAL */}
      <FormProvider {...methods}>
        <form className="container" onSubmit={handleSubmit(onSubmit)}>
          <TransferHeader
            data={{ employeeName, employeePosition, employeeImage }}
            showActionButton={!isCompleted && showActionButton}
            // isUploadedJoiningLetter={isUploadedJoiningLetter}
            isLoading={isLoading}
          />
          <div className="p-10">
            <BasicInfo isDisabled={isDisabled} transfer={transfer} />
            <Letter
              isDisabled={isDisabled}
              name={INTERCHANGE_TRANSFER.SOURCE_EMPLOYEE_LETTER}
            />
            <SupportingDocs
              status={transfer?.status}
              isDisabled={isDisabled}
              canModifyAllTransfer={canModifyAllTransfer}
              canModifyOwnTransfer={showActionButton}
            />
          </div>
          {/* for target employee */}
          <TransferHeader
            data={{
              employeeName: targetEmployeeName,
              employeePosition: targetEmployeePosition,
              employeeImage: targetEmployeeImage,
            }}
            showActionButton={!isCompleted && showActionButton}
            // isUploadedJoiningLetter={isUploadedJoiningLetter}
            isLoading={isLoading}
          />
          <div className="p-10">
            <BasicInfoForTarget isDisabled={isDisabled} transfer={transfer} />
            <Letter
              isDisabled={isDisabled}
              name={INTERCHANGE_TRANSFER.TARGET_EMPLOYEE_LETTER}
            />
            <SupportingDocsForTarget
              status={transfer?.status}
              isDisabled={isDisabled}
              canModifyAllTransfer={canModifyAllTransfer}
              canModifyOwnTransfer={showActionButton}
            />
          </div>
          {(transfer?.status?.toUpperCase() === INITIATED ||
            transfer?.status?.toUpperCase() === NEED_TO_VERIFIED) &&
            showActionButton && (
              <Comments
                comments={
                  transfer
                    ? (transfer.comments as unknown as CommentType[])
                    : []
                }
                isCompleted={isCompleted || !showActionButton}
              />
            )}
          <div className="d-flex justify-content-end gap-8 m-10 border-top pt-10">
            {(transfer?.status?.toUpperCase() === INITIATED ||
              transfer?.status?.toUpperCase() === NEED_TO_VERIFIED) &&
            showActionButton ? (
              <Button
                key="transfer_details_top"
                htmlType="submit"
                fill="fill"
                type="default"
                className="bg-primary-lighter border border-primary-light text-primary"
                loading={isLoading}
              >
                {transfer?.status?.toUpperCase() === INITIATED
                  ? t("INTERCHANGE_TRANSFER.ACTION_BUTTONS.NEED_TO_VERIFIED")
                  : t("INTERCHANGE_TRANSFER.ACTION_BUTTONS.COMPLETE")}
              </Button>
            ) : (
              <Button
                key="transfer_save"
                htmlType="submit"
                fill="fill"
                type="default"
                className="bg-primary border border-primary text-white"
                loading={isLoading}
              >
                {t("INTERCHANGE_TRANSFER.ACTION_BUTTONS.SAVE")}
              </Button>
            )}
          </div>
        </form>
      </FormProvider>
    </>
  );
}
